/**
 * Custom permission constants (non-invasive override).
 *
 * This module re-exports everything from the pristine Chatwoot
 * `constants/permissions.js` and overrides ONLY the constants that need to be
 * aware of our custom RBAC conversation permissions. Core files are never
 * edited: the Vite `customOverridePlugin` resolver transparently redirects
 * imports of `dashboard/constants/permissions` to this file.
 */
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS as BASE_CUSTOM_ROLE_PERMISSIONS,
} from './permissions.js';

export * from './permissions.js';

// The 3 custom RBAC conversation permissions used by our seeded roles. These
// must be recognised by every route/guard that checks conversation access,
// otherwise users with custom roles get stuck in an infinite redirect loop.
export const CUSTOM_RBAC_CONVERSATION_PERMISSIONS = [
  'manage_all_conversations',
  'manage_team_and_assigned_conversations',
  'manage_assigned_conversations_only',
];

// Management permissions added by the fork (RBAC v2): availability lock and
// label administration. Registered on the backend in the granular_rbac /
// availability_rbac initializers.
export const CUSTOM_MANAGEMENT_PERMISSIONS = [
  'agent_availability_manage',
  'label_manage',
  'agent_manage',
  'report_view',
];

// Surface the custom RBAC permissions in the Custom Role editor UI so they can
// be assigned to roles alongside the standard Chatwoot permissions.
export const AVAILABLE_CUSTOM_ROLE_PERMISSIONS = [
  ...BASE_CUSTOM_ROLE_PERMISSIONS,
  ...CUSTOM_RBAC_CONVERSATION_PERMISSIONS,
  ...CUSTOM_MANAGEMENT_PERMISSIONS,
];

export const ASSIGNEE_TYPE_TAB_PERMISSIONS = {
  me: {
    count: 'mineCount',
    // Every conversation-capable role (standard or custom) can see their own
    // assigned conversations, so all 3 custom RBAC permissions are included.
    permissions: [
      ...ROLES,
      ...CONVERSATION_PERMISSIONS,
      ...CUSTOM_RBAC_CONVERSATION_PERMISSIONS,
    ],
  },
  unassigned: {
    count: 'unAssignedCount',
    permissions: [
      ...ROLES,
      MANAGE_ALL_CONVERSATION_PERMISSIONS,
      CONVERSATION_UNASSIGNED_PERMISSIONS,
      // Custom roles with full conversation management also see unassigned.
      'manage_all_conversations',
    ],
  },
  all: {
    count: 'allCount',
    permissions: [
      ...ROLES,
      MANAGE_ALL_CONVERSATION_PERMISSIONS,
      CONVERSATION_PARTICIPATING_PERMISSIONS,
      // Only full-access custom roles can see every conversation.
      'manage_all_conversations',
    ],
  },
};
